import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import {
  getJwtSecondsUntilExpiry,
  isJwtExpiringSoon,
} from '../utils/jwt.util';

/** Exibir aviso quando faltar este tempo (ou menos) para o refresh expirar. */
export const SESSION_RENEWAL_WARNING_SEC = 5 * 60;

export function formatSessionTimeRemaining(seconds: number | null): string {
  if (seconds == null || seconds <= 0) {
    return 'em instantes';
  }
  if (seconds < 60) {
    return 'menos de 1 minuto';
  }
  const mins = Math.ceil(seconds / 60);
  return mins === 1 ? '1 minuto' : `${mins} minutos`;
}

@Injectable({ providedIn: 'root' })
export class SessionRenewalMonitorService {
  private readonly auth = inject(AuthService);
  private readonly notification = inject(NotificationService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly dismissed = signal(false);
  private readonly tick = signal(0);
  readonly renewing = signal(false);

  readonly visible = computed(() => {
    this.tick();
    if (!this.isBrowser || this.dismissed()) {
      return false;
    }
    const refresh = this.auth.getRefreshToken();
    if (!refresh) {
      return false;
    }
    return isJwtExpiringSoon(refresh, SESSION_RENEWAL_WARNING_SEC);
  });

  readonly timeLabel = computed(() => {
    this.tick();
    const refresh = this.auth.getRefreshToken();
    if (!refresh) {
      return '';
    }
    return formatSessionTimeRemaining(getJwtSecondsUntilExpiry(refresh));
  });

  constructor() {
    if (!this.isBrowser) {
      return;
    }

    interval(30_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.evaluate());

    window.addEventListener('focus', () => this.evaluate());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.evaluate();
      }
    });

    this.evaluate();
  }

  dismiss(): void {
    this.dismissed.set(true);
  }

  renew(): void {
    if (this.renewing()) {
      return;
    }

    this.renewing.set(true);
    this.auth.refreshAccessToken().subscribe({
      next: () => {
        this.renewing.set(false);
        this.dismissed.set(false);
        this.evaluate();
        this.notification.success('Sessão renovada com sucesso.');
      },
      error: () => {
        this.renewing.set(false);
      },
    });
  }

  private evaluate(): void {
    this.tick.update((n) => n + 1);

    const refresh = this.auth.getRefreshToken();
    if (!refresh) {
      return;
    }

    const remaining = getJwtSecondsUntilExpiry(refresh);
    if (remaining != null && remaining > SESSION_RENEWAL_WARNING_SEC) {
      this.dismissed.set(false);
    }
  }
}
